// The impact an ember roster card takes when its member is hurt. Kept free of PIXI (like
// roster_speech_lane.ts) so the timings and curves can be unit-tested without a renderer;
// ember_roster.ts owns the drawing and just asks this what a card should look like this frame.
//
// WHY A CARD HAS TO SHOUT. The roster is the only sight the player has of a camp member who is
// fighting somewhere off-screen, and an HP bar 52px wide losing a few pixels is not something
// anyone notices mid-fight. So a hit is drawn as an EVENT rather than a new value: the card jolts,
// washes red, and the health it just lost is left hanging as a pale "ghost" segment that drains
// away a beat later -- the fighting-game trick, which is what makes the size of the hit legible at
// a glance. A rising damage number over the portrait says exactly how much.
//
// Everything is scaled by SEVERITY (the share of the member's maximum health the blow took), so a
// scratch reads as a scratch and a mauling reads as a mauling. The roster only speaks every 500ms
// (ROSTER_INTERVAL_MS), so what arrives is really "the damage taken since the last tick" -- one
// jolt per tick is the most any card can show, and that is the right density for the HUD anyway.

export interface DamageFrame {
    // Card offset, in card-local pixels (the widget's own scale applies on top).
    shakeX: number
    shakeY: number
    // Scale punch, around the card's centre. 1 = at rest.
    scale: number
    // Red wash over the portrait, and the brighter ring around the frame.
    flashAlpha: number
    ringAlpha: number
    // Where the pale "health just lost" segment's right edge sits (0..1 of the bar).
    ghostRatio: number
    // The floating "-N" over the portrait: how far it has risen (px) and how visible it still is.
    numberAlpha: number
    numberRise: number
    amount: number
    // Bumped once per hit, so the widget knows when to re-render the number's text rather than
    // every frame.
    seq: number
}

export const SHAKE_MS = 360
export const FLASH_MS = 300
export const RING_MS = 420
export const PUNCH_MS = 190
// The lost health hangs for a beat before it drains -- that pause is what the eye catches.
export const GHOST_HOLD_MS = 200
export const GHOST_DRAIN_MS = 420
export const NUMBER_MS = 820
// The whole effect is over once its longest part is.
export const EFFECT_MS = NUMBER_MS

// A single blow taking this share of a member's maximum health is "as bad as it gets" as far as the
// card's reaction goes -- above it everything is already at full strength.
export const SEVERE_FRACTION = 0.25

// Even a scratch moves the card (SHAKE_MIN_PX): a hit that produced no reaction at all would read
// as the HUD missing it.
export const SHAKE_MIN_PX = 2
export const SHAKE_MAX_PX = 6.5
export const SHAKE_HZ = 21
export const PUNCH_MIN = 0.04
export const PUNCH_MAX = 0.11
export const FLASH_MAX_ALPHA = 0.55
export const RING_MAX_ALPHA = 0.9
export const NUMBER_RISE_PX = 18
// The number holds at full strength for this share of its life, then fades over the rest.
export const NUMBER_FADE_FROM = 0.45

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)

// Jitter that decays away rather than stopping dead: amplitude falls off quadratically, and the
// vertical component runs at a different rate from the horizontal so the card shivers instead of
// sliding along one axis.
export const shakeAt = (elapsedMs: number, severity: number): { x: number; y: number } => {
    if (elapsedMs >= SHAKE_MS) {
        return { x: 0, y: 0 }
    }
    const decay = 1 - elapsedMs / SHAKE_MS
    const amp = (SHAKE_MIN_PX + (SHAKE_MAX_PX - SHAKE_MIN_PX) * clamp01(severity)) * decay * decay
    const w = (2 * Math.PI * SHAKE_HZ) / 1000
    return { x: amp * Math.sin(elapsedMs * w), y: amp * 0.4 * Math.sin(elapsedMs * w * 1.7 + 1.1) }
}

// One swell up and back down, so the card never ends a hit at the wrong size.
export const punchAt = (elapsedMs: number, severity: number): number => {
    if (elapsedMs >= PUNCH_MS) {
        return 1
    }
    const amount = PUNCH_MIN + (PUNCH_MAX - PUNCH_MIN) * clamp01(severity)
    return 1 + amount * Math.sin(Math.PI * (elapsedMs / PUNCH_MS))
}

// Full the instant the hit lands (that is the impact), then off quickly.
export const flashAt = (elapsedMs: number): number => {
    if (elapsedMs >= FLASH_MS) {
        return 0
    }
    const p = 1 - elapsedMs / FLASH_MS
    return FLASH_MAX_ALPHA * p * p
}

// The ring outlives the wash, so the card stays marked as "this one was hit" a moment longer than
// the flash the eye may have missed.
export const ringAt = (elapsedMs: number): number =>
    elapsedMs >= RING_MS ? 0 : RING_MAX_ALPHA * (1 - elapsedMs / RING_MS)

// Hangs at the pre-hit level, then accelerates down to the real one.
export const ghostAt = (elapsedMs: number, fromRatio: number, toRatio: number): number => {
    if (elapsedMs <= GHOST_HOLD_MS) {
        return fromRatio
    }
    const p = clamp01((elapsedMs - GHOST_HOLD_MS) / GHOST_DRAIN_MS)
    return fromRatio + (toRatio - fromRatio) * p * p
}

// Rises fast and slows as it fades, the way a damage number should.
export const numberAt = (elapsedMs: number): { alpha: number; rise: number } => {
    if (elapsedMs >= NUMBER_MS) {
        return { alpha: 0, rise: NUMBER_RISE_PX }
    }
    const p = elapsedMs / NUMBER_MS
    const alpha = p < NUMBER_FADE_FROM ? 1 : 1 - (p - NUMBER_FADE_FROM) / (1 - NUMBER_FADE_FROM)
    return { alpha, rise: NUMBER_RISE_PX * (1 - (1 - p) * (1 - p)) }
}

interface Pulse {
    elapsedMs: number
    amount: number
    severity: number
    fromRatio: number
    toRatio: number
    seq: number
}

// Per-member hit state for one roster strip. Feed it the health the roster reports (`hit`), advance
// it each frame (`update`), and ask what each card should look like (`frame`).
export class RosterDamageFx {
    private pulses: Map<string, Pulse> = new Map()
    private nextSeq = 1

    // A member's health went DOWN. Healing and first sightings never reach here -- the caller only
    // knows there was a hit because it remembers what the last roster tick said.
    hit = (entityId: string, previousHp: number, hp: number, hpMax: number) => {
        const lost = previousHp - hp
        if (!(hpMax > 0) || !(lost > 0)) {
            return
        }
        const existing = this.pulses.get(entityId)
        const beforeRatio = clamp01(previousHp / hpMax)
        this.pulses.set(entityId, {
            elapsedMs: 0,
            amount: Math.max(1, Math.round(lost)),
            severity: clamp01(lost / hpMax / SEVERE_FRACTION),
            // Struck again mid-effect: start the ghost from wherever it currently stands rather than
            // from the health before THIS hit, or the pale segment would jump back down and the two
            // blows would read as one small one.
            fromRatio: existing
                ? Math.max(beforeRatio, ghostAt(existing.elapsedMs, existing.fromRatio, existing.toRatio))
                : beforeRatio,
            toRatio: clamp01(hp / hpMax),
            seq: this.nextSeq++,
        })
    }

    update = (dtMs: number) => {
        for (const [entityId, pulse] of Array.from(this.pulses.entries())) {
            pulse.elapsedMs += dtMs
            if (pulse.elapsedMs >= EFFECT_MS) {
                this.pulses.delete(entityId)
            }
        }
    }

    // What this member's card should look like, or null when it is at rest (the common case -- the
    // widget then puts the card back to its resting position and stops redrawing it).
    frame = (entityId: string): DamageFrame | null => {
        const pulse = this.pulses.get(entityId)
        if (!pulse) {
            return null
        }
        const { elapsedMs, severity } = pulse
        const shake = shakeAt(elapsedMs, severity)
        const number = numberAt(elapsedMs)
        return {
            shakeX: shake.x,
            shakeY: shake.y,
            scale: punchAt(elapsedMs, severity),
            flashAlpha: flashAt(elapsedMs),
            ringAlpha: ringAt(elapsedMs),
            ghostRatio: ghostAt(elapsedMs, pulse.fromRatio, pulse.toRatio),
            numberAlpha: number.alpha,
            numberRise: number.rise,
            amount: pulse.amount,
            seq: pulse.seq,
        }
    }

    // A member who left the roster takes their effect with them.
    forget = (entityId: string) => {
        this.pulses.delete(entityId)
    }

    activeCount = (): number => this.pulses.size
}
